import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  AlertCircle,
  Check,
  FileText,
  Loader2,
  Receipt,
  Sparkles,
  Tag,
  Truck,
  Users,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Skeleton,
} from '@/components/ui';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { ImportReview, type ReviewSource } from '../onboarding/import-review';
import { LearnFromDocumentsDialog } from './learn-dialog';
import {
  AI_BUDGET_ERROR,
  isRunning,
  learningApi,
  learningKeys,
  type DocType,
  type LearnedSummary,
  type LearningRun,
} from './learning-api';

/**
 * /learn — "למידה ממסמכים" בכל שלב. בלי `run`: הריצה האחרונה שעוד לא נסגרה
 * (כדי שחזרה לדף תמשיך מאיפה שעצרו), או הסבר + העלאה.
 */
export function LearnPage({ runId }: { runId?: string }) {
  const { t } = useTranslation('learning');

  const runs = useQuery({
    queryKey: learningKeys.runs,
    queryFn: ({ signal }) => learningApi.runs(signal),
    enabled: !runId,
  });
  const open = runs.data?.find((r) => r.status !== 'COMPLETED' && r.status !== 'FAILED');
  const activeId = runId ?? open?.id;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={activeId ? <LearnFromDocumentsDialog triggerVariant="secondary" /> : undefined}
      />
      {!runId && runs.isPending ? (
        <Skeleton className="h-64" />
      ) : !runId && runs.isError ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : activeId ? (
        <RunView runId={activeId} />
      ) : (
        <Intro />
      )}
    </div>
  );
}

function Intro() {
  const { t } = useTranslation('learning');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('intro.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="grid gap-3 text-sm sm:grid-cols-3">
          <Item icon={<Receipt aria-hidden />} title={t('intro.sales')}>
            {t('intro.salesText')}
          </Item>
          <Item icon={<Truck aria-hidden />} title={t('intro.purchases')}>
            {t('intro.purchasesText')}
          </Item>
          <Item icon={<Tag aria-hidden />} title={t('intro.priceList')}>
            {t('intro.priceListText')}
          </Item>
        </ul>
        <LearnFromDocumentsDialog
          trigger={
            <Button size="lg" className="w-full">
              <Sparkles aria-hidden />
              {t('intro.start')}
            </Button>
          }
        />
      </CardContent>
    </Card>
  );
}

function RunView({ runId }: { runId: string }) {
  const { t } = useTranslation('learning');
  const qc = useQueryClient();
  const router = useRouter({ warn: false });

  const run = useQuery({
    queryKey: learningKeys.run(runId),
    queryFn: ({ signal }) => learningApi.run(runId, signal),
    refetchInterval: (q) => (isRunning(q.state.data) ? 2_000 : false),
  });

  const source = useMemo<ReviewSource>(
    () => ({
      queryKey: learningKeys.suggestions(runId),
      list: (signal) => learningApi.suggestions(runId, signal),
      update: learningApi.update,
      accept: () => learningApi.accept(runId),
      finish: () => learningApi.complete(runId),
      onDone: async () => {
        await qc.invalidateQueries({ queryKey: learningKeys.all });
        await router?.navigate({ to: '/learn', search: {} });
      },
    }),
    [runId, qc, router],
  );

  if (run.isPending) return <Skeleton className="h-64" />;
  if (run.isError) return <ErrorState error={run.error} onRetry={() => void run.refetch()} />;
  const data = run.data;

  if (isRunning(data)) return <Progress run={data} />;

  if (data.status === 'FAILED') {
    return (
      <Card>
        <CardContent className="space-y-4 py-8 text-center">
          <AlertCircle className="mx-auto size-8 text-danger" aria-hidden />
          <h2 className="text-base font-semibold text-fg">{t('failedRun.title')}</h2>
          <FileList run={data} />
          <LearnFromDocumentsDialog trigger={<Button>{t('failedRun.retry')}</Button>} />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.error === AI_BUDGET_ERROR ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-xs text-warning"
        >
          <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
          <span>{t('budget')}</span>
        </div>
      ) : null}
      {data.summary.files.length > 0 && data.summary.files.every((f) => f.status !== 'done') ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-(--radius-md) border border-danger-border bg-danger-subtle p-3 text-sm text-danger"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            {data.summary.files.some((f) => f.status === 'needs_ai')
              ? t('nothingRead.noAi')
              : data.summary.files.some((f) => f.status === 'scanned_pdf')
                ? t('nothingRead.scanned')
                : t('nothingRead.generic')}
          </span>
        </div>
      ) : null}
      <LearnedCard run={data} />
      {data.status === 'REVIEW' ? (
        <ImportReview
          source={source}
          skipLabel={t('review.discard')}
          doneTitle={t('review.done')}
        />
      ) : null}
    </div>
  );
}

function Progress({ run }: { run: LearningRun }) {
  const { t } = useTranslation('learning');
  const done = run.summary.files.filter((f) => f.status !== 'pending').length;
  return (
    <Card>
      <CardContent className="space-y-5 py-8">
        <div className="text-center" aria-live="polite">
          <Loader2 className="mx-auto mb-2 size-6 animate-spin text-accent" aria-hidden />
          <h2 className="text-base font-semibold text-fg">{t('progress.title')}</h2>
          <p className="mt-1 text-sm text-fg-muted">
            {run.phase === 'summarizing'
              ? t('progress.building')
              : t('progress.count', { done, total: run.filesTotal })}
          </p>
          <p className="mt-1 text-xs text-fg-subtle">{t('progress.hint')}</p>
        </div>
        <FileList run={run} />
      </CardContent>
    </Card>
  );
}

function FileList({ run }: { run: LearningRun }) {
  const { t } = useTranslation('learning');
  return (
    <ul className="mx-auto max-w-md space-y-1.5 text-start">
      {run.summary.files.map((f, i) => {
        const ok = f.status === 'done';
        const pending = f.status === 'pending';
        return (
          <li
            key={i}
            className="flex items-center gap-2 rounded-(--radius-md) border border-border bg-surface px-3 py-2 text-sm"
          >
            <span
              className={cn(
                'grid size-6 shrink-0 place-items-center rounded-full [&_svg]:size-3.5',
                ok && 'bg-success-subtle text-success',
                pending && 'text-fg-subtle',
                !ok && !pending && 'bg-danger-subtle text-danger',
              )}
            >
              {ok ? (
                <Check aria-hidden />
              ) : pending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <X aria-hidden />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span dir="auto" className="block truncate">
                {f.name}
              </span>
              <span className="block text-2xs text-fg-muted">
                {ok && f.docType ? t(`docType.${f.docType}`) : t(`status.${f.status}`)}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** "מה למדנו" — מספרים מהשרת, הטקסט כאן בשפת המשתמש. */
export function LearnedCard({ run }: { run: LearningRun }) {
  const { t } = useTranslation('learning');
  const l: LearnedSummary | null = run.summary.learned;
  const docs = Object.values(l?.documents ?? {}).reduce((a, b) => a + b, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="size-4 text-accent" aria-hidden />
          {t('learned.title')}
        </CardTitle>
        {l && docs > 0 ? (
          <CardDescription>
            {t('learned.documents', { count: docs })}
            {l.dateFrom && l.dateTo
              ? ` · ${t('learned.period', { from: formatDate(l.dateFrom), to: formatDate(l.dateTo) })}`
              : ''}
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {!l || docs === 0 ? (
          <p className="text-sm text-fg-muted">{t('learned.nothing')}</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(l.documents).map(([type, count]) => (
                <Badge key={type} dot={false}>
                  {t(`docType.${type as DocType}`)} · {count}
                </Badge>
              ))}
            </div>
            <dl className="grid gap-3 text-sm sm:grid-cols-3">
              {l.customers > 0 ? (
                <Stat
                  icon={<Users aria-hidden />}
                  label={t('learned.customers', { count: l.customers })}
                />
              ) : null}
              {l.suppliers > 0 ? (
                <Stat
                  icon={<Truck aria-hidden />}
                  label={t('learned.suppliers', { count: l.suppliers })}
                />
              ) : null}
              {l.jobSize ? (
                <Stat
                  icon={<FileText aria-hidden />}
                  label={`${t('learned.jobSize')}: ${formatCurrency(l.jobSize.median)}`}
                  detail={
                    l.jobSize.count > 1
                      ? t('learned.jobSizeRange', {
                          min: formatCurrency(l.jobSize.min),
                          max: formatCurrency(l.jobSize.max),
                        })
                      : undefined
                  }
                />
              ) : null}
              {l.purchaseTotal ? (
                <Stat
                  icon={<Receipt aria-hidden />}
                  label={`${t('learned.purchases')}: ${formatCurrency(l.purchaseTotal)}`}
                />
              ) : null}
            </dl>
            {l.topServices.length > 0 ? (
              <section>
                <h3 className="mb-1.5 text-xs font-medium text-fg-muted">
                  {t('learned.topServices')}
                </h3>
                <ul className="divide-y divide-border rounded-(--radius-md) border border-border">
                  {l.topServices.map((s) => (
                    <li
                      key={s.description}
                      className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <span dir="auto" className="min-w-0 truncate">
                        {s.description}
                      </span>
                      <span className="shrink-0 text-xs text-fg-muted">
                        {s.min !== s.max
                          ? t('learned.range', {
                              min: formatCurrency(s.min),
                              max: formatCurrency(s.max),
                            })
                          : formatCurrency(s.median)}
                        {` · ${t('learned.times', { count: s.count })}`}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
            {l.topSubjects.length > 0 || l.topSuppliers.length > 0 ? (
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                {l.topSubjects.length > 0 ? (
                  <section>
                    <h3 className="mb-1.5 text-xs font-medium text-fg-muted">
                      {t('learned.topSubjects')}
                    </h3>
                    <p dir="auto">{l.topSubjects.map((s) => s.subject).join(' · ')}</p>
                  </section>
                ) : null}
                {l.topSuppliers.length > 0 ? (
                  <section>
                    <h3 className="mb-1.5 text-xs font-medium text-fg-muted">
                      {t('learned.topSuppliers')}
                    </h3>
                    <p dir="auto">{l.topSuppliers.map((s) => s.name).join(' · ')}</p>
                  </section>
                ) : null}
              </div>
            ) : null}
          </>
        )}
        <details className="text-xs text-fg-muted">
          <summary className="cursor-pointer">
            {t('learned.documents', { count: run.filesTotal })}
          </summary>
          <div className="mt-2">
            <FileList run={run} />
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

function Stat({ icon, label, detail }: { icon: ReactNode; label: string; detail?: string }) {
  return (
    <div className="flex items-start gap-2 rounded-(--radius-md) border border-border bg-surface p-3 [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-accent">
      {icon}
      <div>
        <dt className="font-medium text-fg">{label}</dt>
        {detail ? <dd className="text-xs text-fg-muted">{detail}</dd> : null}
      </div>
    </div>
  );
}

function Item({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="rounded-(--radius-md) border border-border bg-surface p-3">
      <div className="mb-1 flex items-center gap-2 font-medium text-fg [&_svg]:size-4 [&_svg]:text-accent">
        {icon}
        {title}
      </div>
      <p className="text-xs text-fg-muted">{children}</p>
    </li>
  );
}
